// =============================================================================
// FEATURES SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { ServiceIcon } from '@/components/ServiceIcon';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { FeaturesComponent } from '@/types/components';

interface FeaturesSectionProps {
  component: ContentComponent;
}


/**
 * Type guard for new FeaturesComponent format
 */
function isNewFeaturesFormat(component: ContentComponent): boolean {
  return (
    component.type === 'features' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Features Section Component
 */
export const FeaturesSection: React.FC<FeaturesSectionProps> = ({ component }) => {
  if (isNewFeaturesFormat(component)) {
    return <FeaturesSectionNew component={component as unknown as any} />;
  }
  return <FeaturesSectionLegacy component={component} />;
};

/**
 * New format Features Section
 */
const FeaturesSectionNew: React.FC<{ component: FeaturesComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section features-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2 className="heading-style">{content.title}</h2>
            {content.description && <p className="body-style">{content.description}</p>}
          </div>
        )}
        
        <div className={`grid grid-cols-${layout.columns || 3}`}>
          {items.map((feature, index) => (
            <div key={feature.id || index} className="feature-item">
              <div className="feature-icon">
                <ServiceIcon
                  icon={feature.icon || 'fi-ts-leadership'}
                  alt={`${feature.title} - ${feature.description}`}
                  title={feature.title}
                  size="lg"
                  className="feature-service-icon"
                />
              </div>
              <h3 className="heading-style">{feature.title}</h3>
              <p className="body-style">{feature.description}</p>
              {feature.image && (
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="feature-image"
                  loading="lazy"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Features Section
 */
const FeaturesSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const features = metadata?.features || [];
  const columns = metadata?.columns || 3;
  
  return (
    <section id={id} className="content-section features-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2 className="heading-style">{title}</h2>
            {content && <p className="body-style">{content}</p>}
          </div>
        )}
        
        <div className={`grid grid-cols-${columns}`}>
          {features.map((feature: any, index: number) => (
            <div key={index} className="feature-item">
              <div className="feature-icon">
                <ServiceIcon
                  icon={feature.icon || 'fi-ts-leadership'}
                  alt={`${feature.title} - ${feature.description}`}
                  title={feature.title}
                  size="lg"
                  className="feature-service-icon"
                />
              </div>
              <h3 className="heading-style">{feature.title}</h3>
              <p className="body-style">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { FeaturesSectionNew, FeaturesSectionLegacy };

